import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import {
  Target,
  Eye,
  Lightbulb,
  Users,
  Award,
  Shield,
  Rocket,
  ArrowRight,
} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Lightbulb,
      title: 'Innovation',
      desc: 'We constantly push the boundaries of creativity and technology to deliver cutting-edge solutions.',
    },
    {
      icon: Users,
      title: 'Collaboration',
      desc: 'We work closely with our clients as partners, ensuring their vision is at the heart of every project.',
    },
    {
      icon: Award,
      title: 'Excellence',
      desc: 'We strive for the highest quality in every deliverable, from concept to deployment.',
    },
    {
      icon: Shield,
      title: 'Integrity',
      desc: 'We build trust through transparency, honesty, and a commitment to doing what\'s right.',
    },
  ];

  const milestones = [
    {
      year: '2026',
      title: 'Our Launch',
      desc: 'We recently launched WicunaTech in 2026—new to the scene, but built on a strong vision and a clear promise to empower businesses with innovative digital solutions.',
    },
    {
      year: 'Our Vision',
      title: 'Lead with Purpose',
      desc: 'To be the digital partner that combines cutting-edge technology with human-centered design, helping every client—from startups to enterprises—achieve their boldest goals.',
    },
    {
      year: 'Our Promise',
      title: 'Commitment from Day One',
      desc: 'We promise transparency, excellence, and results that matter. No shortcuts, no empty words—just dedicated partnership and delivery you can count on.',
    },
  ];

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyem0wLTR2MkgyNHYtMmgxMnoiLz48L2g+PC9nPjwvZz48L3N2Zz4=')] opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            About <span className="text-blue-400">WicunaTech</span>
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            We are a digital solutions company helping businesses transform ideas into powerful, scalable products and experiences.
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Who We <span className="text-blue-600">Are</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                WicunaTech was born from a simple belief: every business deserves access to world-class digital solutions. We combine technical expertise with a deep understanding of business needs to deliver software, design, and strategy that drive real results.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                From startups building their first MVP to enterprises scaling globally, we partner with organizations at every stage. Our team of designers, developers, and strategists works as an extension of your team—focused on your goals and committed to your success.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-square bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center">
                <Rocket className="w-16 h-16 text-white opacity-90" />
              </div>
              <div className="aspect-square bg-rose-100 rounded-2xl" />
              <div className="aspect-square bg-rose-50 rounded-2xl" />
              <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center">
                <Users className="w-16 h-16 text-white opacity-90" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      {/* <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 lg:p-10 text-white">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-blue-100 leading-relaxed">
                To empower businesses with innovative digital solutions that simplify complexity, accelerate growth, and create lasting value. We aim to be the partner our clients trust to turn their boldest ideas into reality.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 lg:p-10 text-white">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-gray-300 leading-relaxed">
                To be a leading force in digital innovation—where technology meets human-centered design, and where every project we deliver sets a new standard for quality, performance, and impact.
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Our Values */}
      <section className="py-16 px-4 bg-rose-50">
        <div className="max-w-7xl mx-auto text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-600">Values</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            The principles that guide everything we do.
          </p>
        </div>
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                <item.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Journey / Milestones */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-600">Journey</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We recently launched in 2026—with a strong vision and a promise to deliver excellence from day one.
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow"
              >
                <span className="text-2xl font-bold text-blue-600">{milestone.year}</span>
                <h3 className="text-xl font-semibold text-gray-900 mt-1">{milestone.title}</h3>
                <p className="text-gray-600 mt-2">{milestone.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Build Something Great?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Let's discuss how we can help you achieve your goals.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-blue-600 font-semibold px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Contact Us
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section> */}

      <Footer />
    </>
  );
};

export default About;
